/**
 * Die Figuren der Tscheppaschlucht — jede eine Klasse, keine Verzweigung.
 *
 * Vererbungsbaum:
 *
 *   AgentPersona (abstrakt, Template Method)
 *     ├─ DrTschaukoPersona     — der Mensch, volles Werkzeug + Ortsbezug
 *     ├─ OwlPersona (abstrakt) — teilt Eulen-Bühne, Ton und Werkzeugsatz
 *     │    ├─ TschaukoOwlPersona  (grüne Eule)
 *     │    └─ TschaukaPersona     (blaue Eule)
 *     └─ UdoPersona            — Klagenfurt-Erzähler, werkzeugfrei
 *
 * Eine weitere Figur = eine Klasse mit vier Angaben. Alles andere erbt sie.
 */

import { TSCHAUKO_BRAIN, TSCHAUKO_GREETING, TSCHAUKO_TOOLS } from '../../../data/tschauko-brain.generated'
import { TSCHAUKA_BRAIN, TSCHAUKA_GREETING, TSCHAUKA_TOOLS } from '../../../data/tschauka-brain.generated'
import {
  TSCHAUKO_OWL_BRAIN,
  TSCHAUKO_OWL_GREETING,
  TSCHAUKO_OWL_TOOLS,
} from '../../../data/tschauko-owl-brain.generated'
import { UDO_BRAIN, UDO_GREETING, UDO_TOOLS } from '../../../data/udo-brain.generated'
import { fetchSessionContext } from '../../../data/tschaukoUserContext'
import { AGENT_GREETINGS, SPRACH_NAME, buildLanguageDirective, currentAgentLanguage } from '../../../data/agentLanguage'
import { getLocationService } from '../../../domain/LocationService'
import { getConfig } from '../../../config/project'
import type { OwlCharacter, OwlSpeechCue } from '../../../components/KeyedOwlAvatar'
import type { GuideEntryContextResolver } from '../GuideEntryContext'
import { resolveGuideEntryContext } from '../guideEntryResolver'
import { AgentPersona, type AgentAvatarStage, type AgentLanguageResolver } from './AgentPersona'

/** Ortsbezug + Fünf-Sprachen-Auflösung teilen sich alle Schlucht-Figuren. */
abstract class TscheppaPersona extends AgentPersona {
  /**
   * Der Name, mit dem die Figur sich vorstellt und den sie nie übersetzt.
   * Vorgabe ist der Anzeigename; Dr. Tschauko trägt den vollen.
   */
  protected properName(): string { return this.displayName }

  /**
   * Die gesprochene Begrüßung in der eingestellten Sprache.
   *
   * WICHTIG (Alex 2026-08-03, „die Eule ist noch immer Dr. Peter Tschauko"):
   * Hier lag der eigentliche Grund, warum sich die Eule weiterhin als der
   * Naturforscher vorstellte. Der Systemtext war längst ihr eigener — aber
   * die Sitzung bekam den Eröffnungssatz aus einer GLOBALEN Konstante
   * (`AGENT_GREETINGS`), und der lautet „Grüß Gott, ich bin Dr. Peter
   * Tschauko". Der Controller setzt diesen Satz wörtlich als Auftrag ein
   * („Begrüße den Besucher kurz mit: …"), also sagte die Eule genau das.
   * Die Begrüßung gehört zur FIGUR, nicht zur Sprache.
   */
  protected greetingForLanguage(_lang: ReturnType<typeof currentAgentLanguage>): string {
    return this.greeting()
  }

  protected language(): AgentLanguageResolver | undefined {
    return () => {
      const lang = currentAgentLanguage()
      return {
        directive: buildLanguageDirective(lang, this.properName()),
        greeting: this.greetingForLanguage(lang),
        name: SPRACH_NAME[lang],
      }
    }
  }

  protected position(): (() => { lat: number; lon: number } | null) | undefined {
    return () => {
      const p = getLocationService().position
      return p ? { lat: p.raw.lat, lon: p.raw.lng } : null
    }
  }

  protected resolveEntryContext(): GuideEntryContextResolver | undefined {
    return resolveGuideEntryContext
  }
}

/** Dr. Peter Tschauko — der Mensch. Bisheriges Verhalten, unverändert. */
export class DrTschaukoPersona extends TscheppaPersona {
  readonly id = 'dr_tschauko'
  readonly displayName = 'Dr. Tschauko'

  protected instructions(): string { return TSCHAUKO_BRAIN }
  protected greeting(): string { return TSCHAUKO_GREETING }
  protected tools(): ReadonlyArray<Record<string, unknown>> {
    return TSCHAUKO_TOOLS as unknown as ReadonlyArray<Record<string, unknown>>
  }
  protected voice(): string { return 'verse' }
  protected contextLine() { return fetchSessionContext }
  protected properName(): string { return 'Dr. Peter Tschauko' }
  /** Er ist die einzige Figur mit gepflegten Übersetzungen der Begrüßung. */
  protected greetingForLanguage(lang: ReturnType<typeof currentAgentLanguage>): string {
    return AGENT_GREETINGS[lang]
  }
  /** `/live` lässt die Darstellung umschalten (Brustbild ↔ Ganzkörper). */
  avatar(): AgentAvatarStage { return { kind: 'arcturian', variant: 'selectable' } }
}

/**
 * Die Eulen. Sie teilen Bühne, Tonfall und Werkzeuge; die Unterklassen
 * bestimmen nur noch Figur, Stimme und Anrede — genau das, was sie
 * unterscheidet.
 */
export abstract class OwlPersona extends TscheppaPersona {
  protected abstract readonly character: OwlCharacter

  /**
   * Wie nah die Figur auf einer Vollbild-Buehne steht. Die Eulen-Clips zeigen
   * viel Luft um das Tier; auf dem Telefon wirkte die Eule dadurch klein und
   * weit weg (Alex 2026-08-04, Bildschirmfoto 117429).
   */
  protected portraitZoom(): number { return 1.18 }

  /** Sichtbares Zeichen beim Sprechen — der Schnabel allein reicht nicht. */
  protected speechCue(): OwlSpeechCue { return 'puffs' }

  avatar(): AgentAvatarStage {
    return {
      kind: 'owl',
      character: this.character,
      portraitZoom: this.portraitZoom(),
      speechCue: this.speechCue(),
    }
  }

  /**
   * Jede Eule hat ein EIGENES Gehirn — erzeugt aus ihrem Persona-Post
   * (`scripts/personas/<id>.config.mjs`, Quelle Content-Post 1268/1269).
   *
   * Bis 2026-08-03 stand hier ein Vorspann vor Dr. Tschaukos fertigem Text:
   * „Du bist Tschauka … das folgende Wissen gilt unverändert" — und darunter
   * weiterhin „Du bist Dr. Peter Tschauko (1852-1904), Naturforscher …".
   * Zwei Identitäten in einem Kopf; die längere gewinnt. Alex am selben Tag:
   * „diese Eule ist nicht Dr. Peter Tschauko". Wissen und Werkzeuge teilen
   * sich die Figuren weiterhin — aber im Bauplan, nicht per Vorwort.
   */
  protected contextLine() { return fetchSessionContext }
}

/** Tschauka — die blaue Eule vom Tschaukofall (Content-Post 1269). */
export class TschaukaPersona extends OwlPersona {
  readonly id = 'tschauka'
  readonly displayName = 'Tschauka'
  protected readonly character: OwlCharacter = 'tschauka'

  protected instructions(): string { return TSCHAUKA_BRAIN }
  protected greeting(): string { return TSCHAUKA_GREETING }
  protected tools(): ReadonlyArray<Record<string, unknown>> {
    return TSCHAUKA_TOOLS as unknown as ReadonlyArray<Record<string, unknown>>
  }
  /**
   * „hell, sprudelnd, wie ein Bergbach" (Persona-Post 1269).
   *
   * NICHT `shimmer` (Alex 2026-08-03: „klingt wie eine erwachsene Frau") —
   * die ist weich und rauchig, also das Gegenteil einer kleinen, neugierigen
   * Eule. `coral` ist unter den Realtime-Stimmen die hellste und lebhafteste.
   * Kindlich klingt keine von ihnen; den Rest macht die Klangregie in
   * Layer 4 (Tempo, kurze Sätze, kleine Ausrufe).
   */
  protected voice(): string { return 'coral' }
}

/** Tschauko — die grüne Eule vom Tschaukofall (Content-Post 1268).
 *  NICHT Dr. Peter Tschauko, der Naturforscher: gleicher Name, andere Figur. */
export class TschaukoOwlPersona extends OwlPersona {
  readonly id = 'tschauko_owl'
  readonly displayName = 'Tschauko'
  protected readonly character: OwlCharacter = 'tschauko'

  protected instructions(): string { return TSCHAUKO_OWL_BRAIN }
  protected greeting(): string { return TSCHAUKO_OWL_GREETING }
  protected tools(): ReadonlyArray<Record<string, unknown>> {
    return TSCHAUKO_OWL_TOOLS as unknown as ReadonlyArray<Record<string, unknown>>
  }
  /** „warm, tief, ruhig" (Persona-Post) — dazu passt ballad. */
  protected voice(): string { return 'ballad' }
}

/** Udo-Tenant: neutraler Klagenfurt-Erzähler, bewusst ohne Schlucht-Werkzeuge. */
export class UdoPersona extends AgentPersona {
  readonly id = 'udo'
  readonly displayName = 'Erzähler'
  protected instructions(): string { return UDO_BRAIN }
  protected greeting(): string { return UDO_GREETING }
  protected tools(): ReadonlyArray<Record<string, unknown>> {
    return UDO_TOOLS as unknown as ReadonlyArray<Record<string, unknown>>
  }
  protected voice(): string { return 'ballad' }
}

/** Die Figuren des aktiven Mandanten — Reihenfolge = Vorgabe zuerst. */
export function tenantPersonas(): AgentPersona[] {
  if (getConfig().id === 'udo') return [new UdoPersona()]
  return [new DrTschaukoPersona(), new TschaukaPersona(), new TschaukoOwlPersona()]
}
